"use client";

import { useMemo, useRef, useState } from "react";
import { useInvoiceStore } from "@/lib/invoice/store";
import { buildProjects, buildSales, readImportFile } from "@/lib/invoice/rowImport";
import { junkReason, normName } from "@/lib/invoice/projectHealth";
import { fmtMoney, num, uid } from "@/lib/invoice/utils";
import { autoTaxMode } from "./InvoiceEditor";
import { BILLING_OPTIONS } from "./options";
import { Button, Input, Modal, Select, cx, useFeedback } from "./ui";

const TEMPLATES = {
  projects: "Client,Type,Project,Service,Rate,Billing,Status,Start date,Category\nStrategic Web Success Group,Project,Zenvy Wellness,20 Keywords,184,Monthly,Active,2026-09-16,SEO\n,Service,Website maintenance,,19,Monthly,Active,,Website\n",
  sales: "Date,Client,Project,Description,Qty,Rate,Category\n2026-09-12,Strategic Web Success Group,Zenvy Wellness,Landing page design,1,150,Website\n",
};

const saleKey = (x) => `${x.clientId}|${x.date}|${normName(x.description)}|${Math.round(num(x.qty) * num(x.rate) * 100)}`;

// "Import" button for the Projects & services and Sales pages. Reads a list
// (Excel/CSV, columns recognised) or invoices (PDF/scan/Excel – client + lines
// only), then shows a review screen: duplicates and lines that look like bank /
// tax details are flagged and left unticked, billing and start date can be set
// for everything at once. Nothing is saved until "Import".
export function RowImportButton({ kind, defaultClientId = "", label = "Import" }) {
  const store = useInvoiceStore();
  const { toast } = useFeedback();
  const ref = useRef(null);
  const [busy, setBusy] = useState(false);
  const [review, setReview] = useState(null);

  const taxFor = (client) => {
    if (!client) return store.settings.defaults.taxRate;
    const mode = client.taxMode || autoTaxMode(client, store.settings, store.brandById(client.brandId));
    return mode === "intra" || mode === "inter" || mode === "vat" ? store.settings.defaults.taxRate : 0;
  };

  const run = async (file) => {
    if (!file) return;
    setBusy(true);
    try {
      const src = await readImportFile(file, kind, { onProgress: (m) => setBusy(typeof m === "string" ? m : true) });
      const batch = uid();
      const from = src.invoices ? (src.invoices.length === 1 ? `invoice ${src.invoices[0].number || file.name}` : `${src.invoices.length} invoices`) : file.name;
      if (kind === "projects") {
        const { projects, clients } = buildProjects(src, store, { batch, defaultClientId, taxFor });
        if (!projects.length) return toast("No projects or services found in this file", { tone: "error" });
        const rows = projects.map((p) => {
          const flag = p._dup ? "Already in your list" : junkReason(p);
          return { key: p.id, item: p, flag, include: !flag };
        });
        setReview({ kind, batch, from, clients, rows });
      } else {
        const order = store.settings.defaults.currency === "USD" ? "mdy" : "dmy";
        const { sales, clients, skipped } = buildSales(src, store, { batch, defaultClientId, order });
        if (!sales.length) return toast(skipped ? (src.invoices ? "Couldn't find the client (Bill to) on this invoice" : `${skipped} row(s) had no client – add a Client column`) : "No rows found to import", { tone: "error" });
        const seen = new Set((store.sales || []).map(saleKey));
        const rows = sales.map((x) => {
          const flag = seen.has(saleKey(x)) ? "Already recorded" : junkReason({ name: x.description, rate: x.rate });
          seen.add(saleKey(x));
          return { key: x.id, item: x, flag, include: !flag };
        });
        setReview({ kind, batch, from, clients, rows });
      }
    } catch (e) {
      toast(e?.message || String(e), { tone: "error" });
    } finally {
      setBusy(false);
    }
  };

  const downloadTemplate = () => {
    const blob = new Blob([TEMPLATES[kind]], { type: "text/csv" });
    const a = document.createElement("a");
    a.href = URL.createObjectURL(blob);
    a.download = `${kind}-import-template.csv`;
    a.click();
    setTimeout(() => URL.revokeObjectURL(a.href), 1000);
  };

  return (
    <>
      <input ref={ref} type="file" accept=".csv,.tsv,.txt,.xlsx,.pdf,.png,.jpg,.jpeg,.webp" className="hidden" data-testid={`import-${kind}`} onChange={(e) => run(e.target.files?.[0]).finally(() => (e.target.value = ""))} />
      <Button variant="secondary" disabled={!!busy} onClick={() => ref.current?.click()} title="A list (Excel / CSV) or your invoices (PDF, scan, Excel) – you review everything before it's saved">
        {busy ? (typeof busy === "string" ? busy : "Reading…") : label}
      </Button>
      <button type="button" className="text-xs text-slate-500 underline-offset-2 hover:text-slate-800 hover:underline" onClick={downloadTemplate}>
        Sample file
      </button>
      {review && <ImportReview review={review} setReview={setReview} />}
    </>
  );
}

const flagCls = (flag) => (/Already/.test(flag) ? "bg-sky-50 text-sky-800 ring-sky-200" : "bg-amber-50 text-amber-800 ring-amber-200");

function ImportReview({ review, setReview }) {
  const store = useInvoiceStore();
  const { toast } = useFeedback();
  const { kind, rows, clients: newClients, batch, from } = review;
  const isProjects = kind === "projects";
  const [show, setShow] = useState("all");
  const allClients = useMemo(() => [...store.clients, ...newClients], [store.clients, newClients]);
  const clientName = Object.fromEntries(allClients.map((c) => [c.id, c.name]));
  const clientCur = Object.fromEntries(allClients.map((c) => [c.id, c.currency || store.settings.defaults.currency]));
  const cur = (item) => clientCur[item.clientId] || store.settings.defaults.currency;

  const setRows = (f) => setReview((r) => ({ ...r, rows: f(r.rows) }));
  const setItem = (key, patch) => setRows((rs) => rs.map((r) => (r.key === key ? { ...r, item: { ...r.item, ...patch } } : r)));
  const setAll = (patch) => setRows((rs) => rs.map((r) => (r.include ? { ...r, item: { ...r.item, ...patch } } : r)));
  const toggle = (key) => setRows((rs) => rs.map((r) => (r.key === key ? { ...r, include: !r.include } : r)));

  const chosen = rows.filter((r) => r.include);
  const dups = rows.filter((r) => /Already/.test(r.flag)).length;
  const junk = rows.filter((r) => r.flag && !/Already/.test(r.flag)).length;
  const visible = rows.filter((r) => show === "all" || (show === "flagged" ? r.flag : !r.flag));
  const clientsIn = [...new Set(chosen.map((r) => r.item.clientId).filter(Boolean))];
  const monthlyValue = {};
  for (const r of chosen) if (r.item.billing === "monthly") monthlyValue[cur(r.item)] = (monthlyValue[cur(r.item)] || 0) + num(r.item.rate) * (num(r.item.qty) || 1);

  const commit = () => {
    if (!chosen.length) return toast("Nothing ticked to import", { tone: "error" });
    const items = chosen.map(({ item }) => {
      const { _dup, ...rest } = item; // eslint-disable-line no-unused-vars
      return isProjects ? { ...rest, rate: num(rest.rate), qty: num(rest.qty) || 1 } : rest;
    });
    const clients = newClients.filter((c) => items.some((x) => x.clientId === c.id));
    store.importBatch(isProjects ? { clients, projects: items } : { clients, sales: items });
    setReview(null);
    const nS = items.filter((p) => p.kind === "service").length;
    toast(
      isProjects
        ? `Imported ${items.length - nS} project(s)${nS ? ` and ${nS} service(s)` : ""} for ${clientsIn.length} client(s)${clients.length ? ` – ${clients.length} new client(s)` : ""}`
        : `Imported ${items.length} sale(s)${clients.length ? ` – ${clients.length} new client(s)` : ""}`,
      { tone: "success", action: { label: "Undo", onClick: () => store.undoImport(batch) } }
    );
  };

  return (
    <Modal
      open
      wide
      onClose={() => setReview(null)}
      title={`Review before importing – ${from}`}
      footer={
        <>
          <span className="mr-auto hidden text-xs text-slate-500 sm:block">Nothing is saved until you click Import. You can undo afterwards.</span>
          <Button variant="secondary" onClick={() => setReview(null)}>Cancel</Button>
          <Button onClick={commit} disabled={!chosen.length} data-testid="review-import">
            Import {chosen.length} {isProjects ? "item" : "sale"}
            {chosen.length === 1 ? "" : "s"}
          </Button>
        </>
      }
    >
      <div className="space-y-4" data-testid="import-review">
        <div className="grid grid-cols-2 gap-2 sm:grid-cols-4">
          {[
            ["Found in file", rows.length, "text-slate-900"],
            ["Ticked to import", chosen.length, "text-emerald-700"],
            [isProjects ? "Already in your list" : "Already recorded", dups, "text-sky-700"],
            ["Look like bank / tax lines", junk, "text-amber-700"],
          ].map(([l, v, c]) => (
            <div key={l} className="rounded-xl bg-slate-50 px-3 py-2 ring-1 ring-slate-200/70">
              <div className="text-[11px] text-slate-500">{l}</div>
              <div className={cx("text-lg font-semibold tabular-nums", c)} data-testid={`review-count-${l.split(" ")[0].toLowerCase()}`}>{v}</div>
            </div>
          ))}
        </div>

        {clientsIn.length > 0 && (
          <div className="text-sm text-slate-600">
            For{" "}
            {clientsIn.map((id) => (
              <b key={id} className="mr-2 text-slate-900">
                {clientName[id]}
                {newClients.some((c) => c.id === id) && <span className="ml-1 rounded bg-blue-50 px-1 py-0.5 text-[10px] font-semibold text-blue-700">NEW CLIENT</span>}
              </b>
            ))}
            {Object.keys(monthlyValue).length > 0 && (
              <span className="text-slate-500">
                · monthly value <b className="text-slate-800">{Object.entries(monthlyValue).map(([c, v]) => fmtMoney(v, c)).join(" + ")}</b>
              </span>
            )}
          </div>
        )}

        {isProjects && (
          <div className="flex flex-wrap items-end gap-3 rounded-xl bg-violet-50/60 p-3 ring-1 ring-violet-100">
            <div className="w-full text-xs font-semibold text-violet-900 sm:w-auto sm:self-center">Set for all ticked rows:</div>
            <label className="text-xs text-slate-600">
              Billing
              <Select className="mt-1 !w-44" value="" onChange={(e) => e.target.value && setAll({ billing: e.target.value })} data-testid="review-billing-all">
                <option value="">Choose…</option>
                {BILLING_OPTIONS.map((b) => (
                  <option key={b.value} value={b.value}>{b.label}</option>
                ))}
              </Select>
            </label>
            <label className="text-xs text-slate-600">
              Type
              <Select className="mt-1 !w-36" value="" onChange={(e) => e.target.value && setAll({ kind: e.target.value })}>
                <option value="">Choose…</option>
                <option value="project">Project</option>
                <option value="service">Service</option>
              </Select>
            </label>
            <label className="text-xs text-slate-600">
              Status
              <Select className="mt-1 !w-36" value="" onChange={(e) => e.target.value && setAll({ status: e.target.value })}>
                <option value="">Choose…</option>
                <option value="active">Active</option>
                <option value="paused">Paused</option>
                <option value="completed">Completed</option>
              </Select>
            </label>
            <label className="text-xs text-slate-600">
              Start date
              <Input type="date" className="mt-1 !w-44" onChange={(e) => setAll({ startDate: e.target.value })} data-testid="review-start-all" />
            </label>
          </div>
        )}

        <div className="flex flex-wrap items-center gap-2 text-xs">
          {[
            ["all", `All ${rows.length}`],
            ["ok", `New ${rows.length - dups - junk}`],
            ["flagged", `Flagged ${dups + junk}`],
          ].map(([k, l]) => (
            <button key={k} onClick={() => setShow(k)} className={cx("rounded-full border px-2.5 py-1 font-medium", show === k ? "border-slate-900 bg-slate-900 text-white" : "border-slate-200 bg-white text-slate-600")}>
              {l}
            </button>
          ))}
          <button className="ml-auto font-medium text-brand-700 hover:underline" onClick={() => setRows((rs) => rs.map((r) => ({ ...r, include: !r.flag })))}>
            Tick only new
          </button>
          <button className="font-medium text-slate-500 hover:underline" onClick={() => setRows((rs) => rs.map((r) => ({ ...r, include: false })))}>
            Untick all
          </button>
        </div>

        <div className="max-h-[46vh] overflow-auto rounded-xl ring-1 ring-slate-200">
          <table className="w-full text-sm">
            <thead className="sticky top-0 z-10 bg-slate-50 text-left text-[11px] font-semibold uppercase tracking-wider text-slate-500">
              <tr>
                <th className="w-8 px-3 py-2" />
                <th className="px-3 py-2">{isProjects ? "Name" : "Description"}</th>
                <th className="px-3 py-2">Client</th>
                {isProjects ? (
                  <>
                    <th className="px-3 py-2">Billing</th>
                    <th className="px-3 py-2">Start date</th>
                  </>
                ) : (
                  <th className="px-3 py-2">Date</th>
                )}
                <th className="px-3 py-2 text-right">Rate</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {visible.map(({ key, item, include, flag }) => (
                <tr key={key} className={cx(!include && "bg-slate-50/70")} data-testid="review-row" data-flag={flag ? "1" : "0"}>
                  <td className="px-3 py-1.5">
                    <input type="checkbox" checked={include} onChange={() => toggle(key)} aria-label="Import this row" />
                  </td>
                  <td className="px-3 py-1.5">
                    <div className={cx("font-medium", include ? "text-slate-900" : "text-slate-400 line-through decoration-slate-300")}>{isProjects ? item.name : item.description}</div>
                    {isProjects && item.service && <div className="text-xs text-slate-500">{item.service}</div>}
                    {flag && <span className={cx("mt-0.5 inline-block rounded px-1.5 py-0.5 text-[10px] font-semibold ring-1 ring-inset", flagCls(flag))}>{flag}</span>}
                  </td>
                  <td className="px-3 py-1.5 text-xs text-slate-600">{item.clientId ? clientName[item.clientId] : <i>Any client</i>}</td>
                  {isProjects ? (
                    <>
                      <td className="px-3 py-1.5">
                        <Select className="!w-40 !py-1 text-xs" value={item.billing} onChange={(e) => setItem(key, { billing: e.target.value })} options={BILLING_OPTIONS} />
                      </td>
                      <td className="px-3 py-1.5">
                        <Input type="date" className="!w-40 !py-1 text-xs" value={item.startDate || ""} onChange={(e) => setItem(key, { startDate: e.target.value })} />
                      </td>
                    </>
                  ) : (
                    <td className="whitespace-nowrap px-3 py-1.5 text-xs">{item.date}</td>
                  )}
                  <td className="whitespace-nowrap px-3 py-1.5 text-right font-medium tabular-nums">{fmtMoney(num(item.rate), cur(item))}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </Modal>
  );
}
